import {AxiosError} from "axios";
import {createContext, useCallback, useEffect, useMemo, useState} from 'react'
import {useNavigate} from "react-router-dom";
import {ApiConfig} from "../api/admin/ApiConfig";
import {SystemApi} from "../api/admin/generated";
import {useSpinnerStatusContext} from "../hook/UseSpinnerStatusContext";
import {logger} from "../utils/Logger";
import {Utils} from "../utils/Utils";

const systemApi = new SystemApi(ApiConfig.config())

export interface IEnterpriseEditionContext {
    enterpriseEdition: boolean | undefined,
    clearEnterpriseEdition: () => void,
}

export const EnterpriseEditionContext = createContext<IEnterpriseEditionContext>({
    enterpriseEdition: false,
    clearEnterpriseEdition: () => {},
});

interface EnterpriseEditionContextProviderProps {
    children: React.ReactNode;
}

export function EnterpriseEditionContextProvider({children} : Readonly<EnterpriseEditionContextProviderProps>)
{
    const [enterpriseEdition, setEnterpriseEdition] = useState<boolean>();
    /**
     * Keeps track of activities for which the spinner should be shown. If an activity is started, the counter should
     * be increased by one and after the activity
     */
    const {setSpinnerStateCounter} = useSpinnerStatusContext();
    /**
     * Navigate is used to navigate to the login page if the admin is no longer authenticated
     */
    const navigate = useNavigate();

    function clearEnterpriseEdition() {
        setEnterpriseEdition(undefined);
    }

    const getEnterpriseEdition = useCallback(() =>
    {
        logger.debug("Getting enterprise edition")

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        systemApi.systemControllerEnterpriseEdition().then(response =>
        {
            setEnterpriseEdition(response.data);
        }).catch((err: Error | AxiosError) => {
            Utils.redirectToLoginIfNotAuthenticated(err, navigate);
        }).finally(() => {
            if (setSpinnerStateCounter !== undefined) {
                setSpinnerStateCounter(count => count - 1);
            }
        })
    }, [setSpinnerStateCounter, navigate])

    useEffect(() => {
        getEnterpriseEdition();
    }, [getEnterpriseEdition]);

    const exposedData = useMemo<IEnterpriseEditionContext>(() => {
        const enterpriseEditionContext : IEnterpriseEditionContext =
        {
            enterpriseEdition : enterpriseEdition,
            clearEnterpriseEdition : clearEnterpriseEdition
        }

        return enterpriseEditionContext;
    }, [enterpriseEdition]);

    return(
        <EnterpriseEditionContext.Provider value = {exposedData}>
            {children}
        </EnterpriseEditionContext.Provider>
    )
}